import { ChatChannel, ChatEvent, ChatMessage } from '../api';

/*
 * Список чатов как состояние, а не как снимок.
 *
 * Раньше список правился только приходом сообщения, поэтому прочтение и
 * присутствие в нём тихо устаревали: в переписке уже две галочки, а слева
 * всё ещё одна; в шапке «давно не заходил», хотя человек в сети. Все правки
 * списка от событий потока собраны здесь — отдельно от компонента, чтобы
 * их можно было проверить тестом, а не глазами на живом сервере.
 */

export type ListCtx = {
  myId: string;
  /** Чат открыт на экране и вкладка в фокусе — сообщение прочитано сразу. */
  seenNow: (channelId: string) => boolean;
};

/** Строка предпросмотра под названием чата. */
export function previewOf(msg: ChatMessage): string {
  if (msg.text) return msg.text;
  if (msg.call) return msg.call.kind === 'video' ? 'Видеозвонок' : 'Звонок';
  if (msg.files.length > 1) return `${msg.files.length} вложения`;
  return msg.files[0] ? msg.files[0].name : '';
}

/**
 * Метка системного уведомления относится к чату, где всё прочитано: такое
 * уведомление пора закрыть. Метки звонков и неизвестных чатов не трогаем.
 */
export function isReadChatTag(tag: string, list: ChatChannel[]): boolean {
  if (!tag.startsWith('chat-')) return false;
  const id = tag.slice('chat-'.length);
  return list.some((c) => c.id === id && c.unread === 0);
}

export function applyEvent(list: ChatChannel[], e: ChatEvent, ctx: ListCtx): ChatChannel[] {
  if (e.type === 'message') return withMessage(list, e.channelId, e.data, ctx);
  if (e.type === 'message.updated') return withEdited(list, e.channelId, e.data);
  if (e.type === 'read') return withRead(list, e.channelId, e.data, ctx);
  if (e.type === 'unread') {
    // остаток считает сервер после каждой отметки (спека 2026-10-02-tabs-devices-sync):
    // ставим как есть, не выводя из прочитанного места самостоятельно
    return list.map((c) => (c.id === e.channelId ? { ...c, unread: e.data.unread } : c));
  }
  if (e.type === 'presence') return withPresence(list, e.data);
  return list;
}

/*
 * Правка последнего сообщения меняет и строку под названием чата: раньше там
 * оставался старый текст, пока не придёт следующее сообщение. Правку любого
 * другого сообщения список не замечает — она в строку не попадает.
 */
function withEdited(list: ChatChannel[], channelId: string, msg: ChatMessage): ChatChannel[] {
  return list.map((c) =>
    c.id === channelId && c.last && c.last.id === msg.id
      ? { ...c, last: { ...c.last, text: previewOf(msg) } }
      : c,
  );
}

function withMessage(
  list: ChatChannel[],
  channelId: string,
  msg: ChatMessage,
  ctx: ListCtx,
): ChatChannel[] {
  const own = msg.author.id === ctx.myId;
  const seen = own || ctx.seenNow(channelId);
  return list.map((c) =>
    c.id === channelId
      ? {
          ...c,
          // ответ из ветки превью не меняет: последняя строка — про саму переписку
          last: msg.parentId ? c.last : {
            id: msg.id,
            text: previewOf(msg),
            authorName: own ? 'Вы' : msg.author.name,
            mine: own,
            ts: msg.ts,
            // только что отправленное ещё никто не читал
            seenByAll: false,
          },
          lastMessageAt: msg.ts,
          unread: seen ? c.unread : c.unread + 1,
          // сервер снимает скрытие вместе с новым сообщением (messages.service),
          // и список обязан это повторить — иначе чат вернётся только после
          // полной перезагрузки, хотя обещано «вернётся с новым сообщением»
          hidden: false,
        }
      : c,
  );
}

function withRead(
  list: ChatChannel[],
  channelId: string,
  data: { userId: string; messageId: number; seenByAll: boolean },
  ctx: ListCtx,
): ChatChannel[] {
  return list.map((c) => {
    if (c.id !== channelId) return c;
    // прочитал я сам — на телефоне или в соседней вкладке; счётчик гасим
    // только если дочитано до известного нам последнего сообщения
    const readToEnd = data.userId === ctx.myId && !!c.last && data.messageId >= c.last.id;
    return {
      ...c,
      unread: readToEnd ? 0 : c.unread,
      // галочки рисуются только у своих сообщений — чужие не трогаем
      last: c.last?.mine ? { ...c.last, seenByAll: data.seenByAll } : c.last,
    };
  });
}

function withPresence(
  list: ChatChannel[],
  data: { userId: string; online: boolean; lastSeen: number },
): ChatChannel[] {
  return list.map((c) =>
    c.kind === 'dm' && c.peer?.id === data.userId
      ? {
          ...c,
          peerOnline: data.online,
          // пока человек в сети, прежнее время визита ещё пригодится: оно
          // станет подписью в тот момент, когда он закроет последнюю вкладку
          peer: data.online ? c.peer : { ...c.peer, lastSeen: data.lastSeen },
        }
      : c,
  );
}
